



























































































import { SvgImage } from '@/app/components';
import { useFeatureFlags, useFilters } from '@/app/composable';
import store from '@/app/store';
import { ClockIcon, PencilAltIcon, TrashIcon } from '@vue-hero-icons/outline';
import {
    AdjustmentsIcon,
    BanIcon,
    CheckIcon,
    DotsHorizontalIcon,
    ExclamationIcon,
    LockClosedIcon,
    XIcon,
} from '@vue-hero-icons/solid';
import { PropType, computed, defineComponent } from '@vue/composition-api';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);
import * as R from 'ramda';
import { TaskExecutionStatus, WorkflowExecutionStatus } from '../constants';
import { IndexedApolloPipeline, IndexedApolloPipelineTask, IndexedHarvesterSourceType } from '../types';
import { CardLayoutCard } from '@/app/components';

export default defineComponent({
    name: 'DataCheckinPipelineOverview',
    components: {
        ClockIcon,
        AdjustmentsIcon,
        CheckIcon,
        DotsHorizontalIcon,
        XIcon,
        ExclamationIcon,
        BanIcon,
        LockClosedIcon,
        SvgImage,
        CardLayoutCard,
    },
    props: {
        pipeline: {
            type: Object as PropType<IndexedApolloPipeline>,
            required: true,
        },
        queryParams: {
            type: String,
            default: '{}',
        },
        alert: {
            type: Object as PropType<{ entityId: string | number; alertId: string; enabled: boolean }>,
            required: false,
        },
    },
    setup(props, { emit, root }: { emit: any; root: any }) {
        const { formatDate, formatDateTime } = useFilters();
        // const selectedQuery = ref(null);
        const { flag } = useFeatureFlags();
        const isAlertsEnabled = flag('alerts');

        const canRetry = computed(() => props.pipeline.executionStatus === WorkflowExecutionStatus.Cancelled);

        const orderedTasks = computed(() => R.sort(R.ascend<any>(R.prop('order')), props.pipeline.tasks));

        const statusTask: any = computed(() => {
            for (let idx = 0; idx < orderedTasks.value.length; idx += 1)
                if ([TaskExecutionStatus.Draft, TaskExecutionStatus.Updating].includes(orderedTasks.value[idx].status))
                    return orderedTasks.value[idx];

            for (let idx = 0; idx < orderedTasks.value.length; idx += 1)
                if (orderedTasks.value[idx].status !== TaskExecutionStatus.Completed) {
                    return orderedTasks.value[idx];
                }

            return orderedTasks.value[orderedTasks.value.length - 1];
        });

        const statusMessage = computed(() => {
            if (canRetry.value) return WorkflowExecutionStatus.Cancelled;
            if (props.pipeline.executionStatus === WorkflowExecutionStatus.Draft) return undefined;
            return props.pipeline.executionStatus;
        });

        const statusClass = computed(() => {
            switch (props.pipeline.executionStatus) {
                case WorkflowExecutionStatus.Updating:
                    return 'text-white bg-purple-500';
                case WorkflowExecutionStatus.Failed:
                    return 'text-white bg-red-600';
                case WorkflowExecutionStatus.Skipped:
                    return 'text-white bg-neutral-600';
                case WorkflowExecutionStatus.Deprecated:
                    return 'text-white bg-cherry-900';
                case WorkflowExecutionStatus.Cancelled:
                    return 'text-white bg-orange-800';
                case WorkflowExecutionStatus.Suspended:
                    return 'text-white bg-red-500';
                case WorkflowExecutionStatus.Completed:
                    return 'text-white bg-green-800';
                case WorkflowExecutionStatus.Queued:
                    return 'text-white bg-slate-500';
                case WorkflowExecutionStatus.Running:
                    return 'text-white bg-slate-700';
                case WorkflowExecutionStatus.Ready:
                    return 'text-white bg-teal-600';
                case WorkflowExecutionStatus.Draft:
                    return 'text-white bg-secondary-700';
                default:
                    return 'text-white bg-secondary-700';
            }
        });

        const taskStatusMap: Record<
            TaskExecutionStatus,
            { label: string; icon: any; attrs?: Record<string, any>; iconClasses?: string }
        > = {
            [TaskExecutionStatus.Updating]: {
                label: 'Updating',
                icon: ExclamationIcon,
                iconClasses: 'text-neutral-600',
            },
            [TaskExecutionStatus.Failed]: {
                label: 'Failed',
                icon: XIcon,
                iconClasses: 'text-red-600',
            },
            [TaskExecutionStatus.Cancelled]: {
                label: 'Cancelled',
                icon: BanIcon,
                iconClasses: 'text-neutral-600',
            },
            [TaskExecutionStatus.Deprecated]: {
                label: 'Deprecated',
                icon: BanIcon,
                iconClasses: 'text-neutral-600',
            },
            [TaskExecutionStatus.Completed]: {
                label: 'Completed',
                icon: CheckIcon,
                iconClasses: 'text-green-800',
            },
            [TaskExecutionStatus.Queued]: {
                label: 'Queued',
                icon: ClockIcon,
                iconClasses: 'text-orange-700',
            },
            [TaskExecutionStatus.Running]: {
                label: 'Running',
                icon: SvgImage,
                attrs: { src: '/img/loader.svg' },
                iconClasses: ' text-primary-500 animate-spin',
            },
            [TaskExecutionStatus.Ready]: {
                label: 'Execution Pending',
                icon: DotsHorizontalIcon,
                iconClasses: 'text-orange-800',
            },
            [TaskExecutionStatus.Draft]: {
                label: 'Under Configuration',
                icon: AdjustmentsIcon,
                iconClasses: 'text-neutral-600',
            },
        };

        const statusMap = {
            [WorkflowExecutionStatus.Updating]: {
                label: 'Updating',
                background: 'bg-purple-500',
                text: 'text-white',
            },
            [WorkflowExecutionStatus.Failed]: {
                label: 'Failed',
                background: 'bg-red-600',
                text: 'text-white',
            },
            [WorkflowExecutionStatus.Skipped]: {
                label: 'Skipped',
                background: 'bg-neutral-600',
                text: 'text-white',
            },
            [WorkflowExecutionStatus.Deprecated]: {
                label: 'Deprecated',
                background: 'bg-cherry-900',
                text: 'text-white',
            },
            [WorkflowExecutionStatus.Cancelled]: {
                label: 'Cancelled',
                background: 'bg-orange-800',
                text: 'text-white',
            },
            [WorkflowExecutionStatus.Suspended]: {
                label: 'Suspended',
                background: 'bg-red-500',
                text: 'text-white',
            },
            [WorkflowExecutionStatus.Completed]: {
                label: 'Completed',
                background: 'bg-green-800',
                text: 'text-white',
            },
            [WorkflowExecutionStatus.Queued]: {
                label: 'Queued',
                background: 'bg-slate-500',
                text: 'text-white',
            },
            [WorkflowExecutionStatus.Running]: {
                label: 'Running',
                background: 'bg-slate-700',
                text: 'text-white',
            },
            [WorkflowExecutionStatus.Draft]: {
                label: 'Draft',
                background: 'bg-slateblue-700',
                text: 'text-white',
            },
            [WorkflowExecutionStatus.Ready]: {
                label: 'Execution Pending',
                background: 'bg-sky-700',
                text: 'text-white',
            },
        };

        const taskConfiguration = computed(() => `configuration:  ${statusTask.value.type}`);

        const harvesterSource = computed((): { label: string; class: string; fileType?: string } | null => {
            let harvesterOption: { label: string; class: string; fileType?: string } | null = null;
            if (props.pipeline?.harvesterSource) {
                switch (props.pipeline.harvesterSource) {
                    case IndexedHarvesterSourceType.Api:
                        harvesterOption = { label: 'Data Provider API', class: 'bg-orange-100 text-orange-900' };
                        break;
                    case IndexedHarvesterSourceType.InternalApi:
                        harvesterOption = { label: 'Platform API', class: 'bg-green-100 text-green-900' };
                        break;
                    case IndexedHarvesterSourceType.File:
                        harvesterOption = {
                            label: 'File',
                            class: 'bg-blue-100 text-blue-900',
                        };
                        break;
                    case IndexedHarvesterSourceType.LargeFiles:
                        harvesterOption = {
                            label: 'Large Files',
                            class: 'bg-slate-100 text-slate-900',
                        };
                        break;
                    case IndexedHarvesterSourceType.Kafka:
                        harvesterOption = {
                            label: 'Kafka',
                            class: 'bg-cherry-100 text-cherry-900',
                        };
                        break;
                    case IndexedHarvesterSourceType.ExternalKafka:
                        harvesterOption = {
                            label: 'External Kafka',
                            class: 'bg-cherry-100 text-cherry-900',
                        };
                        break;
                    case IndexedHarvesterSourceType.MQTT:
                        harvesterOption = {
                            label: 'MQTT',
                            class: 'bg-olive-100 text-olive-900',
                        };
                        break;
                    case IndexedHarvesterSourceType.ExternalMQTT:
                        harvesterOption = {
                            label: 'External MQTT',
                            class: 'bg-olive-100 text-olive-900',
                        };
                        break;
                    case IndexedHarvesterSourceType.BigQuery:
                        harvesterOption = {
                            label: 'BigQuery',
                            class: 'bg-purple-100 text-purple-900',
                        };
                        break;
                    case IndexedHarvesterSourceType.SQL:
                        harvesterOption = {
                            label: 'SQL',
                            class: 'bg-teal-100 text-teal-900',
                        };
                        break;
                    case IndexedHarvesterSourceType.SAPHana:
                        harvesterOption = {
                            label: 'SAP Hana',
                            class: 'bg-slateblue-100 text-slateblue-900',
                        };
                        break;
                    default:
                        return { label: 'No harvester option', class: 'bg-red-100 text-red-900' };
                }

                if (
                    props.pipeline.harvesterSource !== IndexedHarvesterSourceType.InternalApi &&
                    props.pipeline.fileType
                ) {
                    harvesterOption['fileType'] = ['parquet', 'other'].includes(props.pipeline.fileType)
                        ? props.pipeline.fileType
                        : props.pipeline.fileType.toUpperCase();
                }
            }
            return harvesterOption;
        });

        const user = computed(() => store.state.auth.user);
        const isUserPipelineCreator = computed(() => user.value.id === Number(props.pipeline?.createdBy?.id));

        const editPipeline = async (pipeline: any) => {
            if (props.pipeline.executionStatus === WorkflowExecutionStatus.Suspended) {
                (root as any).$toastr.w(
                    `This data check-in pipeline is suspended as its output asset (dataset) was deleted. You need to edit the Loader step and configure a new output asset in order to be able to use this pipeline again.`,
                    'Warning',
                );
            } else {
                emit('edit', pipeline);
            }
        };

        const appendData = async (pipeline: any) => {
            root.$router.push({
                name: 'harvester',
                params: { id: pipeline.id, queryParams: props.queryParams },
            });
        };

        const displayAnyWarnings = (task: IndexedApolloPipelineTask): void => {
            if (task.type === 'harvester' && props.pipeline.executionStatus === WorkflowExecutionStatus.Suspended)
                (root as any).$toastr.w(
                    `This data check-in pipeline is suspended as its output asset (dataset) was deleted. You need to edit the Loader step and configure a new output asset in order to be able to use this pipeline again`,
                    'Warning',
                );
        };

        const toTasks = (name: string, pipeline: any) => {
            return {
                name,
                params: { id: pipeline.id, queryParams: props.queryParams },
            };
        };

        const destroyPipeline = async (pipeline: any) => {
            if (!pipeline.canUse) {
                (root as any).$toastr.e('The Data Check-in Pipeline is locked', 'Error');
            } else {
                emit('destroy', pipeline);
            }
        };

        const goToAsset = (assetId: number) => {
            root.$router.push({ name: 'assets:view', params: { id: assetId } });
        };

        const isFinalized = computed(() => {
            if (props.pipeline.executionStatus === WorkflowExecutionStatus.Suspended) return true;
            return props.pipeline.tasks.every(
                (task: any) =>
                    !!task && ![WorkflowExecutionStatus.Draft, WorkflowExecutionStatus.Updating].includes(task.status),
            );
        });

        const accessLevel = computed(() => props.pipeline && props.pipeline?.accessLevel);

        const actions = computed(() => {
            const actionsArray = [];

            if (isUserPipelineCreator.value) {
                actionsArray.push({
                    id: '1',
                    label: 'Edit',
                    icon: PencilAltIcon,
                    click: () => editPipeline(props.pipeline),
                });
            
                actionsArray.push({
                    id: '2',
                    label: 'Delete',
                    icon: TrashIcon,
                    click: () => destroyPipeline(props.pipeline),
                });
            }

            return actionsArray;
        });

        return {
            IndexedHarvesterSourceType,
            dayjs,
            formatDate,
            formatDateTime,
            statusMessage,
            statusClass,
            WorkflowExecutionStatus,
            TaskExecutionStatus,
            orderedTasks,
            statusTask,
            canRetry,
            isUserPipelineCreator,
            editPipeline,
            appendData,
            toTasks,
            destroyPipeline,
            isFinalized,
            accessLevel,
            harvesterSource,
            isAlertsEnabled,
            goToAsset,
            displayAnyWarnings,
            actions,
            statusMap,
            taskConfiguration,
            taskStatusMap,
        };
    },
});
